// The Implementation Factory on the Gantt's timeline (productivity/Timeline.tsx): one request through the improvement
// cycle (Plan, Do with its tasks and the go-live, Check to the 30-day result), and every request in the cycle on one
// chart on the home page. What's done, where it is today and what's expected, from how long requests here usually take
// at each step.
import { useLoad } from "../hooks";
import { MiniGantt, MiniGanttKey, type MiniRow } from "../productivity/Timeline";
import { factory, CYCLE, STEP_NAME, stepIndex, type RequestDetail, type Timeline } from "./factoryApi";

const day = (iso: string) => iso.slice(0, 10);
const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const fmt = (d: string) => new Date(d + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

/** A step's name. A change to an app is done in DEV and TEST, then released to PROD. */
export function stepName(id: string, pipeline: boolean) {
  if (pipeline && id === "do") return "Do: DEV, TEST, then PROD";
  return STEP_NAME[id] ?? id;
}
/** The step a request's state is at. */
const stepOf = (state: string) => CYCLE[stepIndex(state)]?.id ?? null;

/** "Expected live Nov 9, from how long requests here usually take at each step." */
function liveText(t: Timeline, state: string) {
  if (!t.live) return state === "cancelled" ? "Cancelled." : null;
  if (t.live.done) return `Live since ${fmt(t.live.date)}.`;
  const own = Object.entries(t.fromHistory).some(([, v]) => v);
  return `Expected live ${fmt(t.live.date)}, ${own ? "from how long requests here usually take at each step" : "from starting estimates until there's more history"}.`;
}

/** One request: a row per step, its tasks under Do, the go-live at the end of Do and the result milestones under Check. */
export function RequestTimeline({ d }: { d: RequestDetail }) {
  const t = d.timeline;
  if (!t) return null;
  const today = localToday();
  const rows: MiniRow[] = [];
  for (const s of t.steps) {
    const at = stepOf(d.state);
    if (!s.spans.length && s.id !== at) continue;
    const ms = s.id === "do" && t.live ? [{ date: t.live.date, label: t.live.done ? "Live" : "Expected live", done: t.live.done }]
      : s.id === "check" ? t.milestones : [];
    rows.push({ key: s.id, label: stepName(s.id, d.pipeline), current: s.id === at, spans: s.spans.map((x) => ({ ...x, title: stepName(s.id, d.pipeline) })), milestones: ms });
    if (s.id === "do") {
      for (const k of d.tasks.filter((x) => x.claimedAt && x.state !== "cancelled").slice(0, 8)) {
        const end = day(k.approvedAt ?? k.submittedAt ?? new Date().toISOString());
        rows.push({ key: k.number, sub: true, href: `/factory/t/${k.number}`, label: `${k.number} ${k.title}`,
          spans: [{ start: day(k.claimedAt!), end: end < day(k.claimedAt!) ? day(k.claimedAt!) : end > today && !k.approvedAt ? today : end, kind: k.approvedAt ? "done" : "current", title: `${k.number} ${k.title}` }] });
      }
    }
  }
  const note = liveText(t, d.state);
  return (
    <section className="card if-tl">
      <div className="if-tl-h">
        <h2>Timeline</h2>
        {note && <span className="muted">{note}</span>}
        <span className="sp" />
        <MiniGanttKey />
      </div>
      <MiniGantt rows={rows} label="Step" />
    </section>
  );
}

/** Every open request on one chart: a row each, its steps end to end, and when it's expected live. */
export function FactoryTimeline() {
  const q = useLoad(() => factory.timeline());
  const reqs = q.data?.requests ?? [];
  const rows: MiniRow[] = reqs.map((r) => {
    // To go-live: Check shows once it's there, not as a month of expected bar on every row.
    const spans = r.timeline.steps.flatMap((s) => s.spans.filter((x) => s.id !== "check" || x.kind !== "expected" || stepOf(r.state) === s.id)
      .map((x) => ({ ...x, title: stepName(s.id, r.pipeline) })));
    const live = r.timeline.live;
    return {
      key: r.number, href: `/factory/r/${r.number}`, label: `${r.number} ${r.title}`, spans,
      milestones: live ? [{ date: live.date, label: live.done ? "Live" : "Expected live", done: live.done }] : [],
      note: `${stepName(stepOf(r.state) ?? r.state, r.pipeline)}${live && !live.done ? ` · live ${fmt(live.date)}` : ""}`,
    };
  });
  if (q.data && !rows.length) return null;
  return (
    <section className="card if-tl">
      <div className="if-tl-h">
        <h2>Timeline</h2>
        <span className="muted">Each request in the cycle, Plan to Check: what's done, where it is today and when it's expected live.</span>
        <span className="sp" />
        <MiniGanttKey />
      </div>
      {!q.data ? <div className="empty">{q.error ? "Couldn't load the timeline." : "Loading…"}</div> : <MiniGantt rows={rows} label="Request" />}
    </section>
  );
}
